// History tab (design/screens/patient-history.html): the record's timeline from the audit by-record endpoint (TEN-61) —
// grouped by day, newest first, the changed fields of each edit with the classified ones withheld — and the account-number
// aliases with their validity windows (UC-PATIENTS-008 step 4). Visits and clinical events join the timeline with their modules.
import { controlIdDisplay, type AccountNumberAlias, type AuditActionCode, type AuditEntryView, type PatientView } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { patientHistory, patients } from "../api/endpoints.patients";
import { isApiError } from "../api/problem";
import { formatDate, formatDateTime, formatTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { Chip, Empty, ErrorAlert, Loading, Pager, Panel } from "../ui/bits";
import { CREATED_SOURCE_LABEL, useBranchNames } from "./PatientsShared";

const ACTION_LABEL: Partial<Record<AuditActionCode, string>> = { Create: "Record created", Update: "Record updated", Void: "Voided", Delete: "Deleted", View: "Viewed", Export: "Exported", Print: "Printed", HandoverEnter: "Handed to the kiosk", HandoverExit: "Kiosk hand-over ended" };
const ENTITY_LABEL: Record<string, string> = { patient: "Patient", patient_medical_aid: "Medical aid", patient_document: "Document", patient_status_history: "Status", patient_family_event: "Family", patient_account_number: "Account number", consent_record: "Consent", patient_registration_submission: "Kiosk registration", appointment: "Appointment" };
const CHANGE_REASON_LABEL: Record<AccountNumberAlias["changeReason"], string> = { Created: "Created", DetachNew: "Detached to own account", DetachToExisting: "Moved to another account", Merge: "Merged", ManualCorrection: "Corrected", Import: "Imported" };

/**
 * The patient record's columns in the words of the form, each with the part of the record it belongs to. A column that is not
 * listed — the id, the account-number parts, the normalised search copies, the caches, the family's dependant counter — is
 * bookkeeping: it never appears in a timeline summary.
 */
const FIELDS: Record<string, [label: string, group: string]> = {
  title_code: ["Title", "Name"], initials: ["Initials", "Name"], first_names: ["First names", "Name"], surname: ["Surname", "Name"], known_as: ["Known as", "Name"],
  id_type: ["ID type", "Identity"], id_number: ["ID number", "Identity"], country_of_issue: ["Country of issue", "Identity"], date_of_birth: ["Date of birth", "Identity"],
  gender: ["Gender", "Identity"], id_dob_gender_mismatch_ack: ["ID mismatch acknowledged", "Identity"], card_filing_number: ["Card filing number", "Identity"],
  mobile_display: ["Mobile", "Contact details"], alt_phone_display: ["Alternative phone", "Contact details"], email: ["Email", "Contact details"],
  no_electronic_contact: ["No electronic contact", "Contact details"], preferred_channel: ["Preferred contact", "Contact details"], preferred_language: ["Language", "Contact details"],
  use_main_account_contact: ["Uses the main member's contact", "Contact details"], whatsapp_opt_in: ["WhatsApp opt-in", "Contact details"],
  email_consent: ["Email consent", "Contact details"], sms_consent: ["SMS consent", "Contact details"],
  address_line1: ["Street", "Address"], address_line2: ["Address line 2", "Address"], suburb: ["Suburb", "Address"], city: ["City", "Address"],
  province: ["Province", "Address"], postal_code: ["Postal code", "Address"], country: ["Country", "Address"],
  medical_history: ["Medical history", "Medical history"],
  home_branch_id: ["Home branch", "Home branch"],
  account_code: ["Account code", "Family"], main_account_id: ["Main account", "Family"], relationship_to_main: ["Relationship to the main member", "Family"], is_staff_account: ["Staff account", "Family"],
  account_status: ["Status", "Status"], status_reason_code: ["Status reason", "Status"], status_reason_text: ["Status note", "Status"], is_deceased: ["Deceased", "Status"],
  consent_status_cached: ["Consent", "Consent"],
  recall_due_date: ["Recall due", "Recall"], recall_interval_months_override: ["Recall interval (months)", "Recall"],
  legacy_account_code: ["Legacy code", "Take-on"], legacy_balance_possible: ["Pre-cut-over balance flag", "Take-on"],
};
/** What a membership write did to the medical aid, as the patient's audit entry names it (BR-PATIENTS-034). */
const MEDICAL_AID_CHANGE: Record<string, string> = { Added: "added", Replaced: "replaced", Updated: "updated", Ended: "ended" };
const FLAGS = new Set(["id_dob_gender_mismatch_ack", "no_electronic_contact", "use_main_account_contact", "whatsapp_opt_in", "email_consent", "sms_consent", "is_staff_account", "is_deceased", "legacy_balance_possible"]);
const DATES = new Set(["date_of_birth", "recall_due_date"]);

/** A before or after value as the form would show it: yes/no for a flag, a date, a branch by name; long text shortened. */
function valueOf(key: string, v: unknown, branchName: (id: string) => string | null): string {
  if (v === null || v === undefined || v === "") return "—";
  if (FLAGS.has(key)) return v === 1 || v === true ? "yes" : "no";
  if (DATES.has(key) && typeof v === "string") return formatDate(v);
  if (key === "home_branch_id" && typeof v === "string") return branchName(v) ?? "another branch";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return s.length > 40 ? `${s.slice(0, 39)}…` : s;
}

export interface EntrySummary { title: string; detail: string | null }

/**
 * One timeline entry in plain words (the prototype's patient-history.html): a creation names how the record was made and its
 * account code; an edit names the part of the record it changed ("Address updated") and lists up to four fields as
 * `Label: before → after`, a main account by name only; classified fields are counted, never shown (BR-GOVERNANCE-003); a
 * change to the family's dependant counter alone is the family gaining a dependant. Other entities keep the action's label.
 */
export function summarise(r: AuditEntryView, branchName: (id: string) => string | null): EntrySummary {
  const action = ACTION_LABEL[r.action] ?? r.action;
  const entity = r.entityType !== "patient" ? ` · ${ENTITY_LABEL[r.entityType] ?? r.entityType.replace(/_/g, " ")}` : "";
  const before = r.diff?.before ?? null;
  const after = r.diff?.after ?? null;
  if (r.entityType !== "patient" || (!before && !after)) return { title: `${action}${entity}`, detail: null };
  if (r.action === "Create") {
    const source = typeof after?.["created_source"] === "string" ? CREATED_SOURCE_LABEL[after["created_source"] as keyof typeof CREATED_SOURCE_LABEL] : undefined;
    const code = typeof after?.["account_code"] === "string" ? after["account_code"] : null;
    return { title: action, detail: [source, code].filter(Boolean).join(" · ") || null };
  }
  // A membership write moves the patient's version on and names what it did to the medical aid — never a scheme, number or
  // member (BR-PATIENTS-034): "Medical aid added", "replaced", "updated" or "ended".
  const medicalAid = after?.["medical_aid"];
  if (r.action === "Update" && typeof medicalAid === "string" && MEDICAL_AID_CHANGE[medicalAid]) return { title: `Medical aid ${MEDICAL_AID_CHANGE[medicalAid]}`, detail: null };
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  const named = keys.filter((k) => k in FIELDS);
  const redacted = (r.diff?._redacted ?? []).filter((k) => !keys.includes(k));
  if (named.length === 0 && redacted.length === 0) {
    if (keys.includes("last_dependent_suffix")) return { title: "Dependant added to the family", detail: `suffix /${String(after?.["last_dependent_suffix"] ?? "?")} issued` };
    return { title: action, detail: null };
  }
  const groups = new Set([...named, ...redacted].map((k) => FIELDS[k]?.[1] ?? "Record"));
  const group = groups.size === 1 ? [...groups][0]! : null;
  const title = r.action !== "Update" || !group || group === "Record" ? action : group === "Status" ? "Status changed" : `${group} updated`;
  const lines = named.slice(0, 4).map((k) => (k === "main_account_id" ? `${FIELDS[k]![0]} changed` : `${FIELDS[k]![0]}: ${valueOf(k, before?.[k], branchName)} → ${valueOf(k, after?.[k], branchName)}`));
  const more = named.length > 4 ? ` · +${named.length - 4} more` : "";
  const withheld = redacted.length > 0 ? `${redacted.length} classified field${redacted.length === 1 ? "" : "s"} withheld` : "";
  return { title, detail: [lines.join(" · ") + more, withheld].filter(Boolean).join(" · ") };
}
function actorOf(a: AuditEntryView["actor"]): string {
  if (a.type === "User") return `${a.displayName ?? "user"}${a.controlId !== null ? ` ${controlIdDisplay(a.controlId)}` : ""}`;
  return a.type === "System" ? `System${a.id.startsWith("system:") ? ` · ${a.id.slice(7)}` : ""}` : a.type;
}

export function PatientsHistory({ p }: { p: PatientView }) {
  const sess = useSessionView();
  const { name: branchName } = useBranchNames();
  // The timeline is the audit log's by-record read (`governance.audit.read`, Owner and Admin); other roles see why it is empty.
  const canAudit = can(sess, "governance.audit.read");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const timeline = useQuery({ queryKey: ["patient", p.id, "history", cursor ?? ""], queryFn: () => patientHistory(p.id, { cursor }), enabled: canAudit });
  const aliases = useQuery({ queryKey: ["patient", p.id, "account-numbers"], queryFn: () => patients.accountNumbers(p.id) });
  const rows = timeline.data?.data.items ?? [];
  const days = new Map<string, AuditEntryView[]>();
  for (const r of rows) { const day = formatDate(r.occurredAt); days.set(day, [...(days.get(day) ?? []), r]); }
  const notBuilt = timeline.isError && isApiError(timeline.error) && timeline.error.code === "NOT_FOUND";
  return (
    <div className="split wide">
      <Panel title="Timeline" meta="every logged event on this record · newest first" tight foot={<>
        <span className="help">Entries are written once and never edited; reads of clinical data are logged under POPIA and appear here once the clinical module lands.</span>
        {timeline.data && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!timeline.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, timeline.data!.data.nextCursor!])} />}
      </>}>
        {!canAudit && <Empty title="The change timeline is for an Owner or Admin.">It is read from the audit log, which your role does not open. The account numbers alongside are yours to see.</Empty>}
        {canAudit && timeline.isPending && <div className="panel-body"><Loading what="history" /></div>}
        {timeline.isError && (notBuilt ? <Empty title="The timeline is not available yet." /> : <div className="panel-body"><ErrorAlert error={timeline.error} /></div>)}
        {timeline.data && rows.length === 0 && <Empty title="Nothing logged on this record yet." />}
        {rows.length > 0 && (
          <div className="list">
            {[...days.entries()].map(([day, entries]) => (
              <Fragment key={day}>
                <div className="day">{day}</div>
                {entries.map((r) => {
                  const sum = summarise(r, branchName);
                  return (
                    <div key={r.id} className="item">
                      <span className="t">{formatTime(r.occurredAt)}</span>
                      <span className="et"><Chip tone={r.action === "Void" || r.action === "Delete" ? "crit" : "neutral"}>{ENTITY_LABEL[r.entityType] ?? r.entityType.replace(/_/g, " ")}</Chip></span>
                      <div className="grow">
                        <div className="primary">{sum.title}</div>
                        <div className="sub">{actorOf(r.actor)}{r.authorisedBy && <> · authorised by <span className="mono">{controlIdDisplay(r.authorisedBy.controlId)}</span> {r.authorisedBy.displayName}</>}{r.source !== "UI" && ` · ${r.source}`}{sum.detail && <> · {sum.detail}</>}</div>
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        )}
      </Panel>
      <div className="stack" style={{ gap: 16 }}>
        <Panel title="Account numbers" meta="every code this patient has held" tight foot={<span className="help">A previous code still finds the patient in search (PAT-41).</span>}>
          {aliases.isPending && <div className="panel-body"><Loading what="account numbers" /></div>}
          {aliases.isError && <div className="panel-body"><ErrorAlert error={aliases.error} /></div>}
          {aliases.data && (aliases.data.data.items.length === 0 ? <Empty title="No account-number history." /> : (
            <table>
              <thead><tr><th>Code</th><th>Valid</th><th>Reason</th></tr></thead>
              <tbody>
                {aliases.data.data.items.map((a) => (
                  <tr key={`${a.accountCode}-${a.validFrom}`}>
                    <td className="mono">{a.accountCode}{a.isCurrent && <> <Chip tone="ok" dot>Current</Chip></>}</td>
                    <td className="nw small">{formatDateTime(a.validFrom)}{a.validTo ? <><br />to {formatDateTime(a.validTo)}</> : null}</td>
                    <td>{CHANGE_REASON_LABEL[a.changeReason]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </Panel>
        <Panel title="Since go-live">
          <dl className="kv">
            <dt>Created</dt><dd>{formatDateTime(p.audit.createdAt)}</dd>
            <dt>Last updated</dt><dd>{formatDateTime(p.audit.updatedAt)}</dd>
            <dt>Version</dt><dd className="mono">{p.version}</dd>
            {p.legacyAccountCode && <><dt>Legacy code</dt><dd className="mono">{p.legacyAccountCode}</dd></>}
          </dl>
        </Panel>
      </div>
    </div>
  );
}
